import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Plus, Search, AlertTriangle, CheckCircle, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const aircraftDocTypes = [
    { value: 'airworthiness_certificate', label: 'Certificado de Aeronavegabilidad' },
    { value: 'registration_certificate', label: 'Certificado de Matrícula' },
    { value: 'hull_insurance', label: 'Seguro de Casco' },
    { value: 'liability_insurance', label: 'Seguro de Responsabilidad Civil' },
    { value: 'noise_certificate', label: 'Certificado de Ruido' },
    { value: 'mel', label: 'Lista de Equipo Mínimo (MEL)' },
    { value: 'maintenance_program', label: 'Programa de Mantenimiento' }
];

const crewDocTypes = [
    { value: 'pilot_license', label: 'Licencia de Piloto' },
    { value: 'medical_class1', label: 'Certificado Médico Clase 1' },
    { value: 'medical_class2', label: 'Certificado Médico Clase 2' },
    { value: 'type_rating', label: 'Habilitación de Tipo' },
    { value: 'english_proficiency', label: 'Competencia de Inglés OACI' },
    { value: 'crm_training', label: 'Entrenamiento CRM' },
    { value: 'sms_training', label: 'Entrenamiento SMS' },
    { value: 'dgr_training', label: 'Entrenamiento DGR' },
    { value: 'line_check', label: 'Line Check' },
    { value: 'opc', label: 'OPC' }
];

export default function DocumentsPage() {
    const { hasRole } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [aircraft, setAircraft] = useState([]);
    const [crew, setCrew] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [newDocument, setNewDocument] = useState({
        document_type: '',
        name: '',
        reference_id: '',
        reference_type: 'aircraft',
        issue_date: '',
        expiry_date: '',
        document_number: '',
        issuing_authority: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [docsRes, alertsRes, aircraftRes, crewRes, usersRes] = await Promise.all([
                axios.get(`${API}/documents`),
                axios.get(`${API}/documents/alerts`),
                axios.get(`${API}/aircraft`),
                axios.get(`${API}/crew`).catch(() => ({ data: [] })),
                axios.get(`${API}/users`).catch(() => ({ data: [] }))
            ]);
            setDocuments(docsRes.data);
            setAlerts(alertsRes.data);
            setAircraft(aircraftRes.data);
            setCrew(crewRes.data);
            setUsers(usersRes.data);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateDocument = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...newDocument,
                issue_date: newDocument.issue_date ? new Date(newDocument.issue_date).toISOString() : null,
                expiry_date: new Date(newDocument.expiry_date).toISOString()
            };
            await axios.post(`${API}/documents`, data);
            toast.success('Documento registrado exitosamente');
            setDialogOpen(false);
            fetchData();
            setNewDocument({
                document_type: '', name: '', reference_id: '',
                reference_type: 'aircraft', issue_date: '', expiry_date: '',
                document_number: '', issuing_authority: '', notes: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar documento');
        }
    };

    const getStatusBadge = (doc) => {
        const days = doc.days_until_expiry;
        if (days <= 0) return <Badge className="status-danger">Vencido</Badge>;
        if (days <= 7) return <Badge className="status-danger">{days}d</Badge>;
        if (days <= 30) return <Badge className="status-warning">{days}d</Badge>;
        if (days <= 90) return <Badge className="status-info">{days}d</Badge>;
        return <Badge className="status-success">Vigente</Badge>;
    };

    const getReferenceName = (doc) => {
        if (doc.reference_type === 'aircraft') {
            const ac = aircraft.find(a => a.id === doc.reference_id);
            return ac?.registration || doc.reference_id;
        } else {
            const crewMember = crew.find(c => c.id === doc.reference_id);
            if (crewMember) {
                const user = users.find(u => u.id === crewMember.user_id);
                return user ? `${user.first_name} ${user.last_name}` : doc.reference_id;
            }
            return doc.reference_id;
        }
    };

    const filteredDocuments = documents.filter(doc => {
        const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doc.document_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            getReferenceName(doc).toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' || doc.reference_type === typeFilter;
        const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
        const matchesTab = activeTab === 'all' || 
            (activeTab === 'alerts' && doc.days_until_expiry <= 90) ||
            (activeTab === 'aircraft' && doc.reference_type === 'aircraft') ||
            (activeTab === 'crew' && doc.reference_type === 'crew');
        return matchesSearch && matchesType && matchesStatus && matchesTab;
    });

    // Calculate stats
    const expiredCount = documents.filter(d => d.days_until_expiry <= 0).length;
    const expiringCount = documents.filter(d => d.days_until_expiry > 0 && d.days_until_expiry <= 30).length;
    const warningCount = documents.filter(d => d.days_until_expiry > 30 && d.days_until_expiry <= 90).length;
    const validCount = documents.filter(d => d.days_until_expiry > 90).length;

    if (loading) {
        return <div className="space-y-4 animate-fade-in">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg skeleton-pulse" />
            ))}
        </div>;
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="documents-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                        Control de Documentos
                    </h1>
                    <p className="text-muted-foreground">
                        {documents.length} documentos registrados
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="new-document-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nuevo Documento
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">
                                Registrar Documento
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateDocument} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Tipo de Referencia</Label>
                                    <Select
                                        value={newDocument.reference_type}
                                        onValueChange={(v) => setNewDocument({
                                            ...newDocument, 
                                            reference_type: v,
                                            reference_id: '',
                                            document_type: ''
                                        })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="aircraft">Aeronave</SelectItem>
                                            <SelectItem value="crew">Tripulante</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>{newDocument.reference_type === 'aircraft' ? 'Aeronave' : 'Tripulante'}</Label>
                                    <Select
                                        value={newDocument.reference_id}
                                        onValueChange={(v) => setNewDocument({...newDocument, reference_id: v})}
                                    >
                                        <SelectTrigger data-testid="document-reference-select">
                                            <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {newDocument.reference_type === 'aircraft' ? (
                                                aircraft.map(ac => (
                                                    <SelectItem key={ac.id} value={ac.id}>
                                                        {ac.registration} - {ac.aircraft_type}
                                                    </SelectItem>
                                                ))
                                            ) : (
                                                crew.map(c => {
                                                    const user = users.find(u => u.id === c.user_id);
                                                    return (
                                                        <SelectItem key={c.id} value={c.id}>
                                                            {user ? `${user.first_name} ${user.last_name}` : c.id}
                                                        </SelectItem>
                                                    );
                                                })
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Tipo de Documento</Label>
                                <Select
                                    value={newDocument.document_type}
                                    onValueChange={(v) => {
                                        const docTypes = newDocument.reference_type === 'aircraft' ? aircraftDocTypes : crewDocTypes;
                                        const selected = docTypes.find(t => t.value === v);
                                        setNewDocument({
                                            ...newDocument, 
                                            document_type: v,
                                            name: selected?.label || v
                                        });
                                    }}
                                >
                                    <SelectTrigger data-testid="document-type-select">
                                        <SelectValue placeholder="Seleccionar tipo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(newDocument.reference_type === 'aircraft' ? aircraftDocTypes : crewDocTypes).map(type => (
                                            <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Número de Documento</Label>
                                    <Input
                                        placeholder="DOC-12345"
                                        value={newDocument.document_number}
                                        onChange={(e) => setNewDocument({...newDocument, document_number: e.target.value})}
                                        data-testid="document-number-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Autoridad Emisora</Label>
                                    <Input
                                        placeholder="DGAC, FAA, etc."
                                        value={newDocument.issuing_authority}
                                        onChange={(e) => setNewDocument({...newDocument, issuing_authority: e.target.value})}
                                        data-testid="document-authority-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Fecha de Emisión</Label>
                                    <Input
                                        type="date"
                                        value={newDocument.issue_date}
                                        onChange={(e) => setNewDocument({...newDocument, issue_date: e.target.value})}
                                        data-testid="document-issue-date"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Fecha de Vencimiento *</Label>
                                    <Input
                                        type="date"
                                        value={newDocument.expiry_date}
                                        onChange={(e) => setNewDocument({...newDocument, expiry_date: e.target.value})}
                                        required
                                        data-testid="document-expiry-date"
                                    />
                                </div>
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full btn-primary" 
                                data-testid="create-document-submit"
                                disabled={!newDocument.reference_id || !newDocument.document_type || !newDocument.expiry_date}
                            >
                                Registrar Documento
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">{expiredCount}</p>
                            <p className="text-xs text-muted-foreground uppercase">Vencidos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">{expiringCount}</p>
                            <p className="text-xs text-muted-foreground uppercase">{"<30 días"}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">{warningCount}</p>
                            <p className="text-xs text-muted-foreground uppercase">{"<90 días"}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">{validCount}</p>
                            <p className="text-xs text-muted-foreground uppercase">Vigentes</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="all" data-testid="tab-all-docs">Todos</TabsTrigger>
                    <TabsTrigger value="alerts" data-testid="tab-alerts">
                        Alertas
                        {(expiredCount + expiringCount + warningCount) > 0 && (
                            <Badge className="ml-2 bg-amber-500 text-white">
                                {expiredCount + expiringCount + warningCount}
                            </Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="aircraft" data-testid="tab-aircraft-docs">Aeronaves</TabsTrigger>
                    <TabsTrigger value="crew" data-testid="tab-crew-docs">Tripulación</TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-4 space-y-4">
                    {/* Filters */}
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre, número o referencia..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-documents"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="expired">Vencidos</SelectItem>
                                <SelectItem value="expiring_soon">Por Vencer</SelectItem>
                                <SelectItem value="valid">Vigentes</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Documents List */}
                    <div className="space-y-3">
                        {filteredDocuments.length === 0 ? (
                            <Card className="card-aviation">
                                <CardContent className="py-12 text-center text-muted-foreground">
                                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>No hay documentos</p>
                                </CardContent>
                            </Card>
                        ) : (
                            filteredDocuments.map((doc) => (
                                <Card 
                                    key={doc.id} 
                                    className={`card-aviation border-l-4 ${
                                        doc.days_until_expiry <= 0 ? 'border-l-red-500' :
                                        doc.days_until_expiry <= 7 ? 'border-l-red-500' :
                                        doc.days_until_expiry <= 30 ? 'border-l-amber-500' :
                                        doc.days_until_expiry <= 90 ? 'border-l-blue-500' : 'border-l-emerald-500'
                                    }`}
                                    data-testid={`document-card-${doc.id}`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-4">
                                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                    doc.days_until_expiry <= 30 ? 'bg-red-500/10' :
                                                    doc.days_until_expiry <= 90 ? 'bg-amber-500/10' : 'bg-emerald-500/10'
                                                }`}>
                                                    <FileText className={`w-6 h-6 ${
                                                        doc.days_until_expiry <= 30 ? 'text-red-500' :
                                                        doc.days_until_expiry <= 90 ? 'text-amber-500' : 'text-emerald-500'
                                                    }`} />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-semibold">{doc.name}</span>
                                                        <Badge variant="outline" className="font-mono text-xs">
                                                            {doc.reference_type === 'aircraft' ? 'Aeronave' : 'Tripulante'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">
                                                        {getReferenceName(doc)}
                                                        {doc.document_number && ` • ${doc.document_number}`}
                                                    </p>
                                                    {doc.issuing_authority && (
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            Emitido por: {doc.issuing_authority}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                {getStatusBadge(doc)}
                                                <p className="text-xs text-muted-foreground mt-2">
                                                    Vence: {new Date(doc.expiry_date).toLocaleDateString('es')}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
